import React, { FC } from 'react'

import { CALENDAR, FORM, MEETING_STEPPER } from 'config'
import moment from 'moment-timezone'
import { EditMeetingModal } from './EditMeetingModal'
import { ampli } from '../../../ampli'
import { getRoleFromPath } from '../../../utils/helper'
import MeetingConfirmation from './MeetingConfirmation'
import { UserProfile } from '__generated__/api-types-and-hooks'
import { UserBasicInfo, extendedCalendarEvents } from 'types'
import ModalBox from 'components/Common/ModalBox'
import MeetingUpdatedModalBox from './MeetingUpdatedModalBox'
import MeetingCancelIcon from 'components/Common/SvgIcons/MeetingCancelIcon'
import { CalendarEventViewPreference } from '__generated__/api-types-and-hooks'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { meetingsSchema } from './validations'
import useFormContainer from 'hooks/useFormContainer'

interface ICalendarEventEditModalProps {
  user: UserProfile
  event: extendedCalendarEvents
  timeMin?: string
  timeMax?: string
  closeModal: () => void
  meetingType?: string
  showEditButton: boolean
  setShowEditButton: (param: boolean) => void
  updateMeetingAction: (params) => void
  cancelUserCalendarEventAction: (params) => void
  openMeetingConfirmationModalBoxAction: (input: { error: string; stepType: string }) => void
  stepType: string
  isMeetingDeletion: boolean
  errorMessage: string
  getTackleMeetingEventsAction?: (input: { filter: string; ownerId: string }) => void
  isDisabled: boolean
}

export const CalendarEventEditModal: FC<ICalendarEventEditModalProps> = ({
  user,
  event,
  timeMin,
  timeMax,
  closeModal,
  meetingType,
  showEditButton,
  setShowEditButton,
  updateMeetingAction,
  cancelUserCalendarEventAction,
  openMeetingConfirmationModalBoxAction,
  stepType,
  isMeetingDeletion = false,
  errorMessage = '',
  getTackleMeetingEventsAction,
  isDisabled,
}) => {
  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(meetingsSchema),
  })

  const filter = meetingType?.trim()

  // New handler for EditMeetingContent's onSave
  const handleEditMeetingSave = (data: {
    title: string
    startDateTime: Date
    endDateTime: Date
    attendees: string[]
    teamMembers: string[]
    description: string
    location: string
    meetingLink: string
    timezone: string
  }) => {
    // UpdateUserCalendarEventInput only accepts: tackleId, eventId, title, description, location, eventType, comment, attendees, videoLinkDescription, teamMemberList
    // startDateTime, endDateTime, and timezone are NOT supported - these require rescheduleUserCalendarEvent mutation
    const updateCalendarMeetingPayload = {
      title: data.title,
      eventId: event?.eventId,
      tackleId: event.tackleId,
      location: data.location,
      eventType: event?.eventType,
      attendees: data.attendees, // External attendees (emails)
      teamMemberList: data.teamMembers, // Internal team members (user IDs)
      description: data.description,
      videoLinkDescription: data.meetingLink,
    }
    const getCalendarEventPayload = {
      timeMin,
      timeMax,
      calendarName:
        user.calendarEventViewPreference === CalendarEventViewPreference.Internal
          ? CalendarEventViewPreference.Internal
          : '',
    }
    updateMeetingAction({
      filter,
      getCalendarEventPayload,
      updateCalendarMeetingPayload,
      ownerId: event?.ownerId,
    })
    // Close the modal immediately after triggering save
    closeModal()
  }

  const cancelEventHandler = () => {
    let deleteEventPayload = {
      tackleId: event?.tackleId,
      eventId: event?.eventId,
      eventType: event?.eventType,
    }
    let getEventPayload = {
      calendarName:
        user.calendarEventViewPreference === CalendarEventViewPreference.Internal
          ? CalendarEventViewPreference.Internal
          : '',
      timeMin,
      timeMax,
    }
    let getTackleMeetingPayload = {
      filter: meetingType,
      ownerId: event?.inviteeId,
    }

    cancelUserCalendarEventAction({
      deleteEventPayload,
      getTackleMeetingPayload,
      getEventPayload,
    })
  }

  const cancelMeeting = () => {
    let startTime = moment(event.start as Date, 'hh:mm')
    let endTime = moment(event.end as Date, 'hh:mm')
    const userRole = getRoleFromPath()
    cancelEventHandler()
    ampli.meetingCanceled({
      initiatorRole: userRole,
      initiatorName:
        user?.firstName && user?.lastName ? `${user?.firstName} ${user?.lastName}` : '',
      meetingTitle: event?.title,
      originatorName: event?.organizerName || '',
      duration: endTime.diff(startTime, 'minutes').toString(),
    })
    setShowEditButton(false)
  }

  const deleteMeetingConfirmation = () => {
    openMeetingConfirmationModalBoxAction({
      error: '',
      stepType: MEETING_STEPPER.MEETING_WARNING,
    })
  }

  const participant: UserBasicInfo = {
    firstName:
      user.firstName?.trim().toLowerCase() ===
      event?.organizerDetail?.firstName?.trim().toLowerCase()
        ? event?.participantDetail?.firstName
        : event?.organizerDetail?.firstName,
    lastName:
      user.lastName?.trim().toLowerCase() === event?.organizerDetail?.lastName?.trim().toLowerCase()
        ? event?.participantDetail?.lastName
        : event?.organizerDetail?.lastName,
  }

  const handleCancel = () => {
    openMeetingConfirmationModalBoxAction({
      error: '',
      stepType: '',
    })
  }

  const handleClose = () => {
    handleCancel()
    if (stepType === MEETING_STEPPER.MEETING_CONFIRMATION) {
      closeModal()
    }
  }

  const handleModalClose = () => {
    closeModal()
  }

  const handleConfirmationCloseModal = () => {
    if (filter && getTackleMeetingEventsAction) {
      getTackleMeetingEventsAction({ filter, ownerId: event?.inviteeId || '' })
    }
    closeModal()
  }

  useFormContainer(FORM.MEETING_SCHEDULED, methods)
  return (
    <>
      {(stepType === MEETING_STEPPER.MEETING_CONFIRMATION ||
        stepType === MEETING_STEPPER.MEETING_WARNING) && (
        <ModalBox
          height={'xs:h-full sm:h-auto max-h-full'}
          Icon={MeetingCancelIcon}
          margin="sm:mx-5"
          onClose={handleClose}
        >
          {stepType === MEETING_STEPPER.MEETING_CONFIRMATION && (
            <MeetingUpdatedModalBox
              advisor={participant}
              errorMessage={errorMessage}
              isMeetingDeletion={isMeetingDeletion}
              modalHandler={handleConfirmationCloseModal}
              openMeetingConfirmationModalBoxAction={openMeetingConfirmationModalBoxAction}
            />
          )}

          {stepType === MEETING_STEPPER.MEETING_WARNING && (
            <MeetingConfirmation
              labelText={`Yes, delete`}
              errorMessage=""
              successMessage=""
              isWarning={true}
              handleClose={cancelMeeting}
              handleCancel={handleCancel}
              isLoading={isDisabled}
              Heading={'Are you sure you want to delete?'}
            />
          )}
        </ModalBox>
      )}

      {stepType !== MEETING_STEPPER.MEETING_WARNING &&
        stepType !== MEETING_STEPPER.MEETING_CONFIRMATION && (
          <EditMeetingModal
            onClose={handleModalClose}
            onEdit={() => setShowEditButton(false)}
            onCancel={deleteMeetingConfirmation}
            title={event?.title}
            editMode={!showEditButton}
            eventType={event.eventType as CALENDAR}
            event={event}
            userTimezone={user.timezone || 'UTC'}
            currentUserId={user.id}
            onSave={handleEditMeetingSave}
            isDisabled={isDisabled}
          />
        )}
    </>
  )
}

CalendarEventEditModal.defaultProps = {
  timeMin: '0',
  timeMax: '0',
  showEditButton: true, // Default to true for read-only mode
  setShowEditButton: () => {},
  meetingType: '',
  openMeetingConfirmationModalBoxAction: () => {},
  getTackleMeetingEventsAction: () => {},

  stepType: '',
}
